import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Panel from '../Panel';
import Break from '../Break';
import {
  BlueBorderButton,
  BlueButtonWithWriteAccess as DeleteButton
} from 'components/Button';
import { CONTROL_TYPES, DB_OVER_EXCITED } from 'utils/appConstants';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelButtonRow,
  CurveTableCoordinatesContainer,
  CurvePanelGraphContainer,
  CurveTableCoordinate,
  FullPanel,
  PanelContent
} from 'components/PanelComponents';
import CurvePanelGraph from '../CurvePanelGraph';
import { API_FIELD_MAPPINGS } from 'utils/appConstants';
import { determineBack } from 'utils/determineBack';
import { formatCurveDataValues, isExternallyManaged } from 'utils/format';
import { closeSidePanel, revertSidePanel } from 'reducers/sidePanel/actions';
import DeleteModal from 'components/Modals/DeleteModal';
import { deleteCurve } from 'reducers/curve/actions';
import {
  subscribe,
  unsubscribe,
  POLL_INDIVIDUAL_CURVE
} from 'reducers/polling/actions';
import { TooltipRow } from 'components/Tooltip';
import styled from 'styled-components';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const ButtonRow = styled.div`
  display: flex;
`;

const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);
const SUBSCRIBER_NAME = 'CurvePanel';
const disabledRemoteMessage = 'Remote curves may not be modified';

const CurvePanel = ({ isActive }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [curve, setCurve] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sidePanel = useSelector(state => state.sidePanel);
  const id = useSelector(state => state.sidePanel.id);
  const curves = useSelector(state => state.curve.curves);

  const compileCurveData = useCallback(() => {
    const curve = curves.find(curve => curve.curveId === id);
    setCurve(curve);
  }, [id]);

  useEffect(() => {
    if (isActive) {
      dispatch(
        subscribe({
          subscriber: SUBSCRIBER_NAME,
          pollTypes: [
            {
              type: POLL_INDIVIDUAL_CURVE,
              resourceId: sidePanel.id
            }
          ],
          pollRate
        })
      );
      compileCurveData();
    } else {
      dispatch(unsubscribe(SUBSCRIBER_NAME));
    }
    return () => {
      dispatch(unsubscribe(SUBSCRIBER_NAME));
    };
  }, [dispatch, compileCurveData, isActive, sidePanel.id]);

  useEffect(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, [isLoading, curve]);

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDelete = async () => {
    try {
      setShowDeleteModal(false);
      await dispatch(deleteCurve(id));
      dispatch(closeSidePanel());
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isActive || (isActive && isLoading) || curve === undefined) {
    return null;
  } else {
    const {
      xMultiplier,
      yMultiplier,
      curveDataArray,
      curveId,
      curveType,
      yRefType,
      localCurve: localCurveValue
    } = curve;
    const yRefTypeLabel = API_FIELD_MAPPINGS.Y_REFERENCE.type[yRefType];
    const isRemoteResource =
      externallyManagedEdevs && isExternallyManaged(localCurveValue, true);
    return (
      <>
        <FullPanel>
          <Panel title="Curve Info" primaryHeader />
          <PanelContent>
            <CurvePanelGraphContainer>
              <CurvePanelGraph
                curveData={curveDataArray}
                xAxisLabel={API_FIELD_MAPPINGS.CURVE.type[curveType].xLabel}
                yAxisLabel={
                  parseInt(yRefType) === 0 // N/A yRefType label
                    ? API_FIELD_MAPPINGS.CURVE.type[curveType].yLabel
                    : API_FIELD_MAPPINGS.CURVE.type[curveType].yRefTypes[
                        yRefType
                      ]
                }
                xMultiplier={xMultiplier}
                yMultiplier={yMultiplier}
              />
            </CurvePanelGraphContainer>
            <Break />
            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor />
                <PanelRowDisplay />
              </SubPanelRow>
              <SubPanelRow>
                <PanelRowDescriptor>Curve ID</PanelRowDescriptor>
                <PanelRowDisplay>{curveId}</PanelRowDisplay>
              </SubPanelRow>
              <SubPanelRow>
                <PanelRowDescriptor>Curve Type</PanelRowDescriptor>
                <PanelRowDisplay>
                  {API_FIELD_MAPPINGS.CURVE.type[curveType].displayText}
                </PanelRowDisplay>
              </SubPanelRow>
              <SubPanelRow>
                <PanelRowDescriptor>Y Reference Type</PanelRowDescriptor>
                <PanelRowDisplay>{yRefTypeLabel}</PanelRowDisplay>
              </SubPanelRow>
              {externallyManagedEdevs && (
                <SubPanelRow>
                  <PanelRowDescriptor>Remotely Managed</PanelRowDescriptor>
                  <PanelRowDisplay>
                    {isRemoteResource ? 'True' : 'False'}
                  </PanelRowDisplay>
                </SubPanelRow>
              )}
            </SubPanel>

            <Break margin="10px 0" />

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor alignSelf="flex-start">
                  Curve Points
                </PanelRowDescriptor>
                <PanelRowDisplay>
                  {curveDataArray.map((point, idx) => {
                    return (
                      <CurveTableCoordinatesContainer key={idx}>
                        <CurveTableCoordinate>
                          ({formatCurveDataValues(point.xvalue, xMultiplier)},{' '}
                          {formatCurveDataValues(point.yvalue, yMultiplier)})
                          {parseInt(curveType) ===
                            CONTROL_TYPES.opModWattPFCurveId.curveType && (
                            <div>
                              {point.excitation
                                ? 'Under-excited'
                                : 'Over-excited'}
                            </div>
                          )}
                        </CurveTableCoordinate>
                      </CurveTableCoordinatesContainer>
                    );
                  })}
                </PanelRowDisplay>
              </SubPanelRow>
            </SubPanel>
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              onClick={() =>
                determineBack(
                  sidePanel,
                  () => dispatch(revertSidePanel()),
                  () => dispatch(closeSidePanel())
                )
              }
            >
              Back
            </BlueBorderButton>
            <ButtonRow>
              <TooltipRow
                disabled={isRemoteResource}
                message={disabledRemoteMessage}
              >
                <DeleteButton
                  onClick={() => toggleDeleteModal()}
                  disabled={isRemoteResource}
                >
                  Delete Curve
                </DeleteButton>
              </TooltipRow>
            </ButtonRow>
          </PanelButtonRow>
        </FullPanel>
        {showDeleteModal ? (
          <DeleteModal
            toggleDeleteModal={toggleDeleteModal}
            handleDelete={handleDelete}
          />
        ) : null}
      </>
    );
  }
};

export default CurvePanel;
